/**
 * 接続線の「案内される線名」を画面に見せるための純関数。
 *
 * 放送側 (`lambda/common/ssml_builder.py` の `_resolve_effective_route_name`) は、
 * SSML の `{route_name}` / `{route}` を次の順で決める。
 *
 *   1. `route_nickname_i18n[言語]`
 *   2. `route_nickname_i18n["ja"]` (言語が ja 以外のとき)
 *   3. `route_nickname` (従来の単一文字列 = 日本語扱い)
 *   4. 運行データ (Transformer TripUpdate) の `routeName`
 *   5. `route_name_fallback(route_id, 言語)` (`<route_id>` + 言語別の接尾辞)
 *
 * 1〜3 と 5 は入力値だけで決まるので、画面で確定表示できる。
 * 4 は放送時に運行データから取得するため、画面では分からない。
 * そこで愛称が未設定のときは、4 を使うことと、取得できなかった場合の 5 を併記する。
 *
 * ⚠️ 1〜3 の規則と 5 の接尾辞は、サーバ側と同じにすること。
 *    ズレると「画面の案内」と「実際の放送」が食い違う。
 *    - 1〜3: `resolve_route_nickname_for_language`
 *    - 5:    `_ROUTE_NAME_FALLBACK_SUFFIX_BY_LANGUAGE` / `route_name_fallback`
 */

import type { LocalizedText } from './localizedSsml';

/** `_ROUTE_NAME_FALLBACK_SUFFIX_BY_LANGUAGE` (lambda/common/ssml_builder.py) と同じ表。 */
const ROUTE_NAME_FALLBACK_SUFFIX_BY_LANGUAGE: Readonly<Record<string, string>> = {
  ja: '線',
  zh: '线',
  en: ' Line',
  ko: '선',
};

const DEFAULT_LANGUAGE_KEY = 'ja';

/** 1 言語ぶんの「案内される線名」。 */
export interface RouteNameGuide {
  languageKey: string;
  /**
   * - `nickname`: 愛称が設定されていて、`name` がそのまま案内される。
   * - `default`: 愛称が未設定。運行データの線名で案内し、取得できない場合は
   *   `fallbackName` で案内する。
   */
  source: 'nickname' | 'default';
  /** `source === 'nickname'` のときに案内される名称。 */
  name: string;
  /**
   * 運行データから線名を取得できなかったときに案内される名称。
   * `route_id` が未選択なら空文字。
   */
  fallbackName: string;
}

function clean(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * `route_id` から、線名が取得できなかったときの名称を組み立てる。
 * サーバの `route_name_fallback` と同じ。4 言語以外は日本語に寄せる。
 */
export function routeNameFallback(routeId: string | undefined, languageKey: string): string {
  const id = clean(routeId);
  if (id === '') return '';
  const suffix =
    ROUTE_NAME_FALLBACK_SUFFIX_BY_LANGUAGE[languageKey] ??
    ROUTE_NAME_FALLBACK_SUFFIX_BY_LANGUAGE[DEFAULT_LANGUAGE_KEY];
  return `${id}${suffix}`;
}

/**
 * 放送言語ごとに、案内される線名を求める。
 *
 * @param routeId 接続線のメイン route_id (未選択なら空文字 / undefined)
 * @param nickname 日本語の愛称 (`route_nickname`)
 * @param nicknameI18n 言語別の愛称 (`route_nickname_i18n`)
 * @param languages 放送言語。空なら日本語だけを対象にする
 */
export function resolveRouteNameGuides(
  routeId: string | undefined,
  nickname: unknown,
  nicknameI18n: LocalizedText | undefined,
  languages: readonly string[],
): RouteNameGuide[] {
  const targets = languages.length > 0 ? languages : [DEFAULT_LANGUAGE_KEY];
  const dict: LocalizedText = nicknameI18n && typeof nicknameI18n === 'object' ? nicknameI18n : {};

  return targets.map((languageKey) => {
    // サーバの resolve_route_nickname_for_language と同じ順序。
    const name =
      clean(dict[languageKey]) ||
      (languageKey !== DEFAULT_LANGUAGE_KEY ? clean(dict[DEFAULT_LANGUAGE_KEY]) : '') ||
      clean(nickname);
    return {
      languageKey,
      source: name === '' ? 'default' : 'nickname',
      name,
      fallbackName: routeNameFallback(routeId, languageKey),
    };
  });
}
